import React from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, Users, BookOpen, ClipboardList, ArrowRight, CheckCircle, Accessibility } from 'lucide-react';
import { images } from '../data/mockData';

const PublicEntities = () => {
  const services = [
    {
      icon: FileCheck,
      title: 'TAC 202 Readiness',
      description: 'Understanding what the rules actually mean for your organization, including NIST/TAC-aligned readiness reviews'
    },
    {
      icon: Users,
      title: 'Staff Training',
      description: 'Security training people will understand and remember'
    },
    {
      icon: BookOpen,
      title: 'Documentation Support',
      description: 'Help creating documentation that passes audits'
    },
    {
      icon: ClipboardList,
      title: 'Risk Understanding',
      description: 'Plain explanation of what risks matter and what to do about them'
    }
  ];

  const challenges = [
    'Figuring out what TAC 202 actually requires in plain English',
    'Creating documentation that makes sense and passes audits',
    'Training staff without confusing them with technical terms',
    'Making good decisions within procurement rules and budgets',
    'Staying ready for audits without constant stress'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-stone-100 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6 leading-tight">
                For Texas Public Entities
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Help making sense of TAC 202 and other requirements. Explained clearly, without confusion.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors"
              >
                Discuss your needs
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
            <div>
              <img
                src={images.publicSector.library}
                alt="Public institution"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4 text-center">
            Common Challenges We Help With
          </h2>
          <p className="text-lg text-gray-600 mb-12 text-center">
            Public entities face unique technology and compliance requirements
          </p>
          <div className="space-y-4">
            {challenges.map((challenge, index) => (
              <div key={index} className="flex items-start bg-stone-50 p-6 rounded-lg">
                <CheckCircle size={24} className="text-[#c84630] mr-4 flex-shrink-0 mt-1" />
                <p className="text-gray-700 text-lg">{challenge}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Provide */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-12 text-center">
            What We Provide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-lg border border-gray-200">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#f5e6e0] rounded-lg mb-4">
                  <service.icon size={28} className="text-[#c84630]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAC 202 Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={images.publicSector.community}
                alt="Community collaboration"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">
                TAC 202 Compliance Made Clear
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                TAC 202 requires certain cybersecurity controls. We explain what this means for you in regular language.
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">
                    What you need to know
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    We explain what applies to you and what doesn't. No guesswork.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">
                    Documentation that works
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    We help you create documentation that satisfies auditors without unnecessary complexity.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">
                    Staying ready
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    You'll know what to expect and how to stay ready as requirements change.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Staff Training Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">
              Staff Security Training
            </h2>
            <p className="text-lg text-gray-600">
              Training that people actually understand and remember
            </p>
          </div>
          <div className="bg-white p-8 rounded-lg border-2 border-[#c84630]">
            <p className="text-gray-700 mb-6 leading-relaxed">
              Most security training is forgettable. We provide training people understand and remember because it makes sense for how they actually work.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-[#1e3a5f] mb-2">Topics we cover:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    Password security that makes sense
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    Recognizing phishing and scams
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    Safe handling of sensitive information
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    What to do when something seems wrong
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-[#1e3a5f] mb-2">How we train:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    In-person sessions at your location
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    Plain language anyone can understand
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    Real examples relevant to your work
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#c84630] mr-2">•</span>
                    Time for questions and discussion
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Procurement Respect Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">
            We Respect Your Procurement Environment
          </h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            We work within your established processes. Our guidance is clear and defensible for audits.
          </p>
          <p className="text-gray-600 leading-relaxed">
            No sales pressure. No vendor conflicts. Just straight advice about what makes sense.
          </p>
        </div>
      </section>

      {/* Accessibility & Public Sector Compatibility Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-4 mt-1">
              <Accessibility size={20} className="text-gray-600" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3">
                Accessibility & Public Sector Compatibility
              </h3>
              <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
                <p>
                  Accessibility compliance matters for Texas agencies. We understand that public organizations must meet specific standards when engaging with vendors and service providers.
                </p>
                <p>
                  This website is designed to meet Texas Administrative Code (TAC) §206 and WCAG 2.2 Level AA accessibility requirements. We build our materials and guidance with compliance considerations in mind, so your engagement with us aligns with your organization's accessibility obligations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Let's discuss your compliance needs
          </h2>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            Tell us about your TAC 202 situation or other concerns. We'll talk through what would help.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors text-lg"
          >
            Contact us
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PublicEntities;
